import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Collapse,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Timeline,
  Title,
} from '@mantine/core';
import { IconCheck, IconMap, IconMapPin, IconPlus, IconTruck } from '@tabler/icons-react';
import { Link, useParams } from 'react-router-dom';
import type {
  CargoEventCreateInput,
  ShipmentDetail,
  ShipmentStatus,
} from '@expeditor/shared';
import { cargoEventCreateSchema } from '@expeditor/shared';
import { shipmentsApi } from '../../shared/api/shipments';
import { useAuthStore } from '../../shared/auth/auth.store';
import { ShipmentMap } from './ShipmentMap';
import { PaymentsSection } from '../payments/PaymentsSection';
import { PaymentDemandsSection } from '../payments/PaymentDemandsSection';
import { DeliveryLegsSection } from '../delivery-legs/DeliveryLegsSection';
import { ShipmentUnitsSection } from './ShipmentUnitsSection';
import { ShipmentLegsSection } from './ShipmentLegsSection';

const SHIPMENT_STATUS_COLOR: Record<ShipmentStatus, string> = {
  PROJECT: 'grape',
  CREATED: 'gray',
  PICKED_UP: 'blue',
  IN_TRANSIT: 'orange',
  ARRIVED: 'cyan',
  DELIVERED: 'green',
  CLOSED: 'teal',
  CANCELLED: 'red',
};
const SHIPMENT_STATUS_LABEL: Record<ShipmentStatus, string> = {
  PROJECT: 'Проект',
  CREATED: 'Создана',
  PICKED_UP: 'Принят к перевозке',
  IN_TRANSIT: 'В пути',
  ARRIVED: 'Прибыл',
  DELIVERED: 'Выдан',
  CLOSED: 'Закрыта',
  CANCELLED: 'Отменена',
};

const EVENT_TYPES: { value: CargoEventCreateInput['type']; label: string }[] = [
  { value: 'RECEIVED', label: 'Принят к перевозке' },
  { value: 'HANDLING', label: 'Обработка' },
  { value: 'LOADED', label: 'Погружен' },
  { value: 'STORED', label: 'Хранение' },
  { value: 'IN_TRANSIT', label: 'Промежуточная точка' },
  { value: 'UNLOADED', label: 'Выгружен' },
  { value: 'HANDED_OVER', label: 'Передан получателю' },
  { value: 'DELIVERED', label: 'Доставлен' },
];

export function ShipmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const user = useAuthStore((s) => s.user);
  const isStaff = user && ['MANAGER', 'ADMIN', 'AUDITOR'].includes(user.role);
  const isManager = user && ['MANAGER', 'ADMIN'].includes(user.role);

  const [shipment, setShipment] = useState<ShipmentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // event modal
  const [eventOpen, setEventOpen] = useState(false);
  const [evType, setEvType] = useState<CargoEventCreateInput['type']>('RECEIVED');
  const [evOccurredAt, setEvOccurredAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [evLocation, setEvLocation] = useState('');
  const [evLat, setEvLat] = useState<number | string>('');
  const [evLon, setEvLon] = useState<number | string>('');
  const [evComment, setEvComment] = useState('');

  // Карта свёрнута по умолчанию — раскрывается кнопкой.
  const [mapOpen, setMapOpen] = useState(false);

  const reload = async () => {
    if (!id) return;
    try {
      const data = await shipmentsApi.getOne(id);
      setShipment(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить');
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const togglePossession = async (value: boolean) => {
    if (!shipment) return;
    try {
      setShipment(await shipmentsApi.setPossession(shipment.id, value));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить отметку');
    }
  };

  const onClose = async () => {
    if (!shipment) return;
    if (!confirm('Закрыть перевозку? Это запустит ретеншн 3 года.')) return;
    setBusy(true);
    try {
      const updated = await shipmentsApi.close(shipment.id);
      setShipment(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось закрыть');
    } finally {
      setBusy(false);
    }
  };

  const onAddEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shipment) return;
    const dto: CargoEventCreateInput = {
      type: evType,
      occurredAt: new Date(evOccurredAt).toISOString(),
      locationName: evLocation || undefined,
      latitude: evLat === '' ? undefined : Number(evLat),
      longitude: evLon === '' ? undefined : Number(evLon),
      comment: evComment || undefined,
    };
    const parsed = cargoEventCreateSchema.safeParse(dto);
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => i.message).join('; '));
      return;
    }
    setBusy(true);
    try {
      await shipmentsApi.addCargoEvent(shipment.id, parsed.data);
      setEventOpen(false);
      setEvLocation('');
      setEvLat('');
      setEvLon('');
      setEvComment('');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось добавить событие');
    } finally {
      setBusy(false);
    }
  };


  if (error && !shipment) return <Alert color="red">{error}</Alert>;
  if (!shipment) return <Text c="dimmed">Загрузка…</Text>;

  // Показывать карту есть смысл, только если есть что показывать.
  const eventsWithGeo = shipment.events.filter((e) => e.latitude != null && e.longitude != null).length;
  const hasGeo = shipment.geoTrack.length > 0 || eventsWithGeo > 0;

  return (
    <Stack>
      <Group justify="space-between">
        <Group>
          <Title order={2}>
            {shipment.contractNumber
              ? `Перевозка по договору ${shipment.contractNumber}`
              : `Перевозка (проект) по поручению ${shipment.orderNumber}`}
          </Title>
          <Badge color={SHIPMENT_STATUS_COLOR[shipment.status]} size="lg">
            {SHIPMENT_STATUS_LABEL[shipment.status]}
          </Badge>
        </Group>
        {isManager && shipment.status === 'DELIVERED' && (
          <Button color="teal" leftSection={<IconCheck size={16} />} onClick={onClose} loading={busy}>
            Закрыть перевозку
          </Button>
        )}
      </Group>

      {error && (
        <Alert color="red" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card withBorder>
        <Group>
          <Text>Поручение:</Text>
          <Text
            component={Link}
            c="blue"
            to={`${isStaff ? '/manager' : '/cabinet'}/orders/${shipment.orderId}`}
          >
            {shipment.orderNumber}
          </Text>
          <Text c="dimmed">·</Text>
          <Text>Груз: {shipment.cargoName}</Text>
        </Group>
        {/* Отметка приёма груза во владение — определяет, от чьего имени
            (экспедитора/клиента) формируются документы плеч: поручение/ЭТрН/ЭЗЗ. */}
        <Group mt="xs">
          {isManager ? (
            <Checkbox
              label="Экспедитор принимает груз во владение (документы плеч — от имени экспедитора, иначе от имени клиента)"
              checked={shipment.expeditorTakesPossession ?? false}
              onChange={(e) => void togglePossession(e.currentTarget.checked)}
            />
          ) : shipment.expeditorTakesPossession ? (
            <Badge color="grape" variant="light">
              Приём во владение экспедитором
            </Badge>
          ) : null}
        </Group>
        <Group mt="xs">
          {shipment.startedAt && (
            <Text size="sm" c="dimmed">
              В пути с: {new Date(shipment.startedAt).toLocaleString('ru-RU')}
            </Text>
          )}
          {shipment.finishedAt && (
            <Text size="sm" c="dimmed">
              Доставлен: {new Date(shipment.finishedAt).toLocaleString('ru-RU')}
            </Text>
          )}
        </Group>
      </Card>

      {/* Плечи перевозки (фаза 2): участок маршрута + исполнитель + себестоимость.
          Только для staff — исполнители и себестоимость (смета) внутренние,
          клиенту не показываются. */}
      {isStaff && (
        <Card withBorder>
          <ShipmentLegsSection
            shipmentId={shipment.id}
            editable={Boolean(isManager)}
            onChanged={() => void reload()}
          />
        </Card>
      )}

      {/* Документы по плечам перевозки: ЭТрН / поручение на каждое плечо. */}
      {isManager && (
        <DeliveryLegsSection
          source={{ kind: 'shipmentLeg', shipmentId: shipment.id, legs: shipment.legs }}
        />
      )}

      <ShipmentUnitsSection
        shipment={shipment}
        canEdit={Boolean(isManager) && shipment.status !== 'CLOSED' && shipment.status !== 'CANCELLED'}
        onSaved={setShipment}
      />

      <Card withBorder>
        <Group justify="space-between" mb="sm">
          <Title order={4}>События груза</Title>
          {isManager && shipment.status !== 'CLOSED' && shipment.status !== 'CANCELLED' && (
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setEventOpen(true)}>
              Добавить событие
            </Button>
          )}
        </Group>
        {shipment.events.length === 0 ? (
          <Text c="dimmed" size="sm">
            Событий груза ещё не было.
          </Text>
        ) : (
          <Timeline active={shipment.events.length - 1} bulletSize={20}>
            {shipment.events.map((e) => (
              <Timeline.Item
                key={e.id}
                bullet={<IconMapPin size={12} />}
                title={EVENT_TYPES.find((t) => t.value === e.type)?.label ?? e.type}
              >
                <Text size="sm" c="dimmed">
                  {new Date(e.occurredAt).toLocaleString('ru-RU')}
                  {e.locationName ? ` · ${e.locationName}` : ''}
                </Text>
                {e.comment && <Text size="sm" mt={4}>{e.comment}</Text>}
              </Timeline.Item>
            ))}
          </Timeline>
        )}
      </Card>

      <PaymentsSection shipmentId={shipment.id} canManage={Boolean(isManager)} />

      <PaymentDemandsSection shipmentId={shipment.id} canManage={Boolean(isManager)} />


      {/* Карта — в самом низу и свёрнута: развёрнутая она занимала пол-экрана
          между содержательными блоками. Раскрывается кнопкой. */}
      <Card withBorder>
        <Group justify="space-between">
          <div>
            <Title order={4}>Маршрут на карте</Title>
            <Text size="xs" c="dimmed">
              {hasGeo
                ? `Точек трека: ${shipment.geoTrack.length}, событий с координатами: ${eventsWithGeo}`
                : 'Координат пока нет — карта появится с первыми точками трека или событиями с координатами'}
            </Text>
          </div>
          <Button
            variant="default"
            size="xs"
            disabled={!hasGeo}
            leftSection={<IconMap size={14} />}
            onClick={() => setMapOpen((o) => !o)}
          >
            {mapOpen ? 'Скрыть карту' : 'Показать карту'}
          </Button>
        </Group>
        <Collapse in={mapOpen && hasGeo}>
          <Box mt="sm">
            <ShipmentMap events={shipment.events} track={shipment.geoTrack} />
          </Box>
        </Collapse>
      </Card>

      {/* Модал: событие */}
      <Modal opened={eventOpen} onClose={() => setEventOpen(false)} title="Новое событие груза">
        <form onSubmit={onAddEvent}>
          <Stack>
            <Select
              label="Тип события"
              required
              data={EVENT_TYPES.map((t) => ({ value: t.value, label: t.label }))}
              value={evType}
              onChange={(v) => v && setEvType(v as CargoEventCreateInput['type'])}
            />
            <TextInput
              label="Время"
              type="datetime-local"
              required
              value={evOccurredAt}
              onChange={(e) => setEvOccurredAt(e.currentTarget.value)}
            />
            <TextInput
              label="Локация (название)"
              value={evLocation}
              onChange={(e) => setEvLocation(e.currentTarget.value)}
            />
            <Group grow>
              <NumberInput
                label="Широта"
                value={evLat}
                onChange={setEvLat}
                decimalScale={6}
                min={-90}
                max={90}
              />
              <NumberInput
                label="Долгота"
                value={evLon}
                onChange={setEvLon}
                decimalScale={6}
                min={-180}
                max={180}
              />
            </Group>
            <Textarea
              label="Комментарий"
              value={evComment}
              onChange={(e) => setEvComment(e.currentTarget.value)}
              minRows={2}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEventOpen(false)}>
                Отмена
              </Button>
              <Button type="submit" loading={busy}>
                Сохранить
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {!isStaff && shipment.status === 'CLOSED' && (
        <Alert color="teal">
          Перевозка завершена. Контракт закрыт, начался ретеншн данных по требованиям ПП 1317.
        </Alert>
      )}
    </Stack>
  );
}
